import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Modal,
  PrimaryButton,
  SecondaryButton,
} from "../UI/DesignStandards";
import SquadSelect from "../ScoringHub/ScoringToolbar/SquadSelect";
import { LAYOUT_ONE, LAYOUT_PACK } from "../../utility/recapSheets";
import { downloadRecapSheetPdf } from "./downloadRecapSheetPdf";

const PrintRecapSheetDialog = ({
  isOpen,
  onClose,
  tournamentId,
  tournamentName,
  squads = [],
  events = [],
  teams = [],
  defaultSquadId = "",
}) => {
  const [squadId, setSquadId] = useState("");
  const [eventIds, setEventIds] = useState([]);
  const [layout, setLayout] = useState(LAYOUT_ONE);
  const [printing, setPrinting] = useState(false);
  const [error, setError] = useState("");
  const dialogOpenRef = useRef(false);
  const layoutRef = useRef(LAYOUT_ONE);
  layoutRef.current = layout;

  const squadEvents = useMemo(() => {
    const ids = new Set(
      (teams || [])
        .filter((team) => team.squadID === squadId && team.eventsID)
        .map((team) => team.eventsID)
    );
    return (events || []).filter(
      (event) => event.isPrimary && ids.has(event.id)
    );
  }, [teams, events, squadId]);

  const assignedEventIds = useMemo(
    () =>
      new Set(
        (teams || [])
          .filter(
            (team) =>
              team.squadID === squadId && team.startingPair && team.eventsID
          )
          .map((team) => team.eventsID)
      ),
    [teams, squadId]
  );

  useEffect(() => {
    if (!isOpen) {
      dialogOpenRef.current = false;
      return;
    }
    const justOpened = !dialogOpenRef.current;
    dialogOpenRef.current = true;
    const preferred =
      defaultSquadId &&
      (squads || []).some((squad) => squad.id === defaultSquadId)
        ? defaultSquadId
        : (squads || [])[0]?.id || "";
    if (justOpened) {
      setError("");
      setSquadId(preferred);
      return;
    }
    setSquadId((current) =>
      current && (squads || []).some((squad) => squad.id === current)
        ? current
        : preferred
    );
  }, [isOpen, defaultSquadId, squads]);

  const assignedEventKey = [...assignedEventIds].sort().join(",");
  const squadEventKey = squadEvents.map((event) => event.id).join(",");

  useEffect(() => {
    if (!isOpen) return;
    setEventIds(
      squadEvents
        .filter((event) => assignedEventIds.has(event.id))
        .map((event) => event.id)
    );
    // Reset when the dialog opens or the squad's assigned events change, not on each checkbox click.
  }, [isOpen, squadId, squadEventKey, assignedEventKey]);

  const selectedSquad = (squads || []).find((squad) => squad.id === squadId);

  const toggleEvent = (eventId, checked) => {
    setEventIds((current) => {
      if (checked) {
        return current.includes(eventId) ? current : [...current, eventId];
      }
      return current.filter((id) => id !== eventId);
    });
  };

  const handlePrint = async () => {
    if (!squadId) {
      setError("Choose a squad.");
      return;
    }
    if (!eventIds.length) {
      setError("Choose an event.");
      return;
    }
    try {
      setPrinting(true);
      setError("");
      await downloadRecapSheetPdf({
        tournamentId,
        tournamentName,
        squadId,
        squadDate: selectedSquad?.squadDate,
        layout: layoutRef.current === LAYOUT_PACK ? LAYOUT_PACK : LAYOUT_ONE,
        eventIds,
      });
      onClose();
    } catch (printError) {
      setError(printError.message || "Could not create the PDF.");
    } finally {
      setPrinting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Print Recaps"
      subtitle="Assigned pairs on this squad. Staff write game scores."
      maxWidth="max-w-lg"
      footer={
        <div className="flex justify-end gap-2">
          <SecondaryButton onClick={onClose} disabled={printing}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            onClick={handlePrint}
            loading={printing}
            disabled={printing || !squadId || !eventIds.length}
          >
            Print
          </PrimaryButton>
        </div>
      }
    >
      <SquadSelect
        label="Squad"
        className="mb-4"
        squads={squads || []}
        value={squadId}
        onChange={setSquadId}
      />
      {squadEvents.length ? (
        <fieldset className="mb-4 space-y-2">
          <legend className="block text-sm font-medium text-brand-charcoal mb-2">
            Event
          </legend>
          {squadEvents.map((event) => {
            const hasAssigned = assignedEventIds.has(event.id);
            return (
              <label key={event.id} className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={eventIds.includes(event.id)}
                  disabled={!hasAssigned}
                  onChange={(change) =>
                    toggleEvent(event.id, change.target.checked)
                  }
                />
                <span>
                  {event.eventName}
                  {hasAssigned ? "" : " (no assigned pairs)"}
                </span>
              </label>
            );
          })}
        </fieldset>
      ) : null}
      <fieldset className="mb-4 space-y-2">
        <legend className="block text-sm font-medium text-brand-charcoal mb-2">
          Layout
        </legend>
        <label className="flex items-start gap-2 text-sm">
          <input
            type="radio"
            name="recap-layout"
            value={LAYOUT_ONE}
            className="mt-1"
            checked={layout === LAYOUT_ONE}
            onChange={() => setLayout(LAYOUT_ONE)}
          />
          <span>One pair per page</span>
        </label>
        <label className="flex items-start gap-2 text-sm">
          <input
            type="radio"
            name="recap-layout"
            value={LAYOUT_PACK}
            className="mt-1"
            checked={layout === LAYOUT_PACK}
            onChange={() => setLayout(LAYOUT_PACK)}
          />
          <span>Pack pairs on a page</span>
        </label>
      </fieldset>
      <p className="text-sm text-brand-muted mb-2">
        Unassigned teams are left off. If you check two events, each event
        prints as its own recaps in one file. Names, averages, and handicap come
        from the assignment. Each block prints BTA Use and a short team code so
        Import Recaps can find the team if lanes or names change later.
      </p>
      {error ? <p className="text-sm text-red-600">{error}</p> : null}
    </Modal>
  );
};

export default PrintRecapSheetDialog;
